import { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import TrendPeriodToggle, { TrendPeriod } from './TrendPeriodToggle';
import TrendSparkline, { SparklineDataPoint, ThresholdConfig } from './TrendSparkline';
import { useChartColors } from '@/hooks/useChartColors';
import AnimatedCounter from '@/components/ui/animated-counter';

export interface TrendData {
  value: number;
  isPositive: boolean;
  label?: string; // e.g., "vs last week", "vs last month"
  sparklineData?: SparklineDataPoint[];
  threshold?: ThresholdConfig;
}

export interface StatItem {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  color?: string;
  trend?: TrendData;
  onClick?: () => void;
}

interface QuickStatsWidgetProps {
  stats: StatItem[];
  columns?: 2 | 3 | 4 | 5 | 6;
  isUpdating?: boolean;
  className?: string;
  trendPeriod?: TrendPeriod;
  onTrendPeriodChange?: (period: TrendPeriod) => void;
}

export default function QuickStatsWidget({ 
  stats, 
  columns = 6,
  isUpdating = false,
  className,
  trendPeriod,
  onTrendPeriodChange
}: QuickStatsWidgetProps) {
  const chartColors = useChartColors();
  
  const gridCols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 xl:grid-cols-4',
    5: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
    6: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6',
  };

  const showToggle = trendPeriod && onTrendPeriodChange;

  return (
    <div className="space-y-3">
      {showToggle && (
        <div className="flex justify-end">
          <TrendPeriodToggle value={trendPeriod} onChange={onTrendPeriodChange} />
        </div>
      )}
      <div className={cn(
        'grid grid-cols-1 gap-4',
        gridCols[columns],
        className
      )}>
      {stats.map((stat, index) => {
        const IconComponent = stat.icon;
        const isClickable = !!stat.onClick;
        
        return (
          <Card 
            key={index} 
            className={cn(
              'relative overflow-hidden transition-all duration-300',
              chartColors.cardClassName,
              isUpdating && 'ring-2 ring-green-500/30',
              isClickable && 'cursor-pointer hover:shadow-md hover:border-blue-300'
            )}
            onClick={stat.onClick}
          >
            {/* Live update indicator */}
            {isUpdating && (
              <div className="absolute top-2 right-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                </span>
              </div>
            )}
            
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-gray-600">
                {stat.title}
              </CardTitle>
              <div className={cn(
                'p-1.5 rounded-lg',
                stat.color ? `bg-${stat.color}/10` : 'bg-primary/10'
              )}>
                <IconComponent className={cn(
                  'h-3.5 w-3.5',
                  stat.color || 'text-primary'
                )} />
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              {/* Sparkline */}
              {stat.trend?.sparklineData && stat.trend.sparklineData.length > 1 && (
                <TrendSparkline 
                  data={stat.trend.sparklineData} 
                  isPositive={stat.trend.isPositive}
                  height={24}
                  className="mb-1.5 -mx-1"
                  threshold={stat.trend.threshold}
                />
              )}
              
              <div className="flex items-end justify-between gap-1">
                <div className="min-w-0 flex-1">
                  <div className="text-base sm:text-lg font-bold text-gray-900 truncate">
                    {typeof stat.value === 'number' ? (
                      <AnimatedCounter value={stat.value} duration={1200} />
                    ) : stat.value}
                  </div>
                  {stat.description && (
                    <p className="text-[10px] mt-0.5 text-gray-500 truncate">
                      {stat.description}
                    </p>
                  )}
                </div>
                
                {stat.trend && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={cn(
                          'flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0',
                          stat.trend.value === 0 && 'bg-gray-100 text-gray-500',
                          stat.trend.value !== 0 && stat.trend.isPositive && 'bg-green-100 text-green-700',
                          stat.trend.value !== 0 && !stat.trend.isPositive && 'bg-red-100 text-red-700'
                        )}>
                          {stat.trend.value === 0 ? (
                            <Minus className="h-2.5 w-2.5" />
                          ) : stat.trend.isPositive ? (
                            <TrendingUp className="h-2.5 w-2.5" />
                          ) : (
                            <TrendingDown className="h-2.5 w-2.5" />
                          )}
                          <span>{stat.trend.value === 0 ? '0' : (stat.trend.isPositive ? '+' : '')}{stat.trend.value}%</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent style={chartColors.tooltipStyle}>
                        <p>{stat.trend.label || 'vs previous period'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
      </div>
    </div>
  );
}
